import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

export default function ServicesPage() {
  const services = [
    {
      icon: 'ri-bank-card-line',
      title: 'Buy Tradelines',
      description: 'Strategic authorized user tradelines designed to strengthen your credit profile and improve your fundability before applying for business or personal funding.',
      details: [
        {
          subtitle: 'Authorized User Tradelines',
          text: 'Access to premium authorized user tradelines that can help strengthen your credit profile and improve your fundability positioning.'
        },
        {
          subtitle: 'Aged High-Limit Accounts',
          text: 'Carefully selected tradelines with established history and substantial credit limits to maximize impact on your credit profile.'
        },
        {
          subtitle: 'Profile Strengthening Strategy',
          text: 'Strategic placement of tradelines designed to address specific weaknesses in your credit profile and improve overall fundability.'
        },
        {
          subtitle: 'Posting Window Coordination',
          text: 'Precise timing coordination to ensure tradelines post during optimal windows for your funding timeline and application strategy.'
        },
        {
          subtitle: 'Inventory-Based Availability',
          text: 'Real-time inventory management ensures you have access to available tradelines that match your specific profile needs.'
        },
        {
          subtitle: 'Fundability-Focused Placement',
          text: 'Every tradeline placement is evaluated through the lens of fundability - ensuring it contributes to your overall approval probability.'
        }
      ],
      image: 'https://public.readdy.ai/ai/img_res/cb67681228aa75a2792514f3a5d25233.jpg'
    },
    {
      icon: 'ri-user-line',
      title: 'Personal Funding',
      description: 'Personal credit optimization and strategic funding solutions',
      details: [
        {
          subtitle: 'Personal Loans',
          text: 'Strategic positioning for personal loan approvals with favorable terms and competitive interest rates.'
        },
        {
          subtitle: '0% Personal Credit Cards',
          text: 'Access to premium credit cards with 0% introductory periods and valuable rewards programs.'
        },
        {
          subtitle: 'Credit Profile Optimization',
          text: 'Comprehensive analysis and strategic improvements to your personal credit profile to maximize approval rates.'
        },
        {
          subtitle: 'Strategic Positioning',
          text: 'Expert guidance on timing, application strategy, and profile presentation to lenders.'
        },
        {
          subtitle: 'Approval Rate Maximization',
          text: 'Data-driven approach to ensure you\'re applying for the right products at the right time with the highest probability of approval.'
        }
      ],
      image: 'https://readdy.ai/api/search-image?query=clean%20modern%20personal%20finance%20consultation%20setting%20with%20professional%20advisor%20and%20client%20discussing%20credit%20strategy%20in%20elegant%20minimalist%20office%20with%20navy%20blue%20accents%20representing%20trustworthy%20personal%20funding%20services%20and%20financial%20planning&width=800&height=600&seq=service-personal-001&orientation=landscape'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Business Credit Building',
      description: 'Complete business credit infrastructure development',
      details: [
        {
          subtitle: 'Entity Setup & Compliance',
          text: 'Proper business entity formation and compliance requirements to establish a fundable business foundation.'
        },
        {
          subtitle: 'DUNS Registration',
          text: 'Dun & Bradstreet registration and profile optimization to establish your business credit identity.'
        },
        {
          subtitle: 'Tradeline Development',
          text: 'Strategic establishment of vendor accounts and trade credit to build a strong business credit history.'
        },
        {
          subtitle: 'Bank Account Seasoning',
          text: 'Proper business banking setup and account management to demonstrate financial stability to lenders.'
        },
        {
          subtitle: 'Fundability Audits',
          text: 'Comprehensive review of your business structure, compliance, and credit profile to identify and fix fundability issues.'
        }
      ],
      image: 'https://readdy.ai/api/search-image?query=sophisticated%20business%20credit%20building%20concept%20with%20modern%20corporate%20charts%20and%20financial%20growth%20metrics%20in%20clean%20minimalist%20style%20with%20navy%20blue%20and%20charcoal%20tones%20representing%20professional%20credit%20infrastructure%20development%20and%20institutional%20compliance&width=800&height=600&seq=service-credit-001&orientation=landscape'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <main className="pt-20">
        <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
            <p className="text-xs uppercase tracking-wider text-gray-600 mb-4 font-semibold">
              OUR SERVICES
            </p>
            <h1 className="text-5xl md:text-6xl font-bold text-navy mb-6">
              Strategic Funding Solutions
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive funding strategy and business credit consulting across three core disciplines
            </p>
          </div>
        </section>

        {services.map((service, index) => (
          <section
            key={index}
            className={`py-20 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
          >
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 flex items-center justify-center">
                      <i className={`${service.icon} text-4xl text-navy`}></i>
                    </div>
                    <h2 className="text-4xl font-bold text-navy">
                      {service.title}
                    </h2>
                  </div>
                  
                  <p className="text-lg text-gray-600 mb-8">
                    {service.description}
                  </p>

                  <div className="space-y-6">
                    {service.details.map((detail, idx) => (
                      <div key={idx}>
                        <h3 className="text-lg font-semibold text-navy mb-2">
                          {detail.subtitle}
                        </h3>
                        <p className="text-sm text-gray-600 leading-relaxed">
                          {detail.text}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="rounded-xl overflow-hidden shadow-lg">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>
        ))}

        <section className="py-24 bg-navy text-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-lg text-gray-300 mb-10">
              Let's assess your funding needs and create a strategic plan
            </p>
            <Link
              to="/apply"
              className="inline-flex items-center bg-white text-navy px-8 py-4 rounded-lg text-base font-medium hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Apply Now
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
